import dayjs from 'dayjs';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Table, Typography, Input, Space, Button, Select, message } from 'antd';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { adminOperationsService, adminRoomService, offersService } from '../../services/adminService';
import { formatLkr } from '../../utils/currency';
import { ViewGuestProfileModal } from '../frontdesk/components/ViewGuestProfileModal';
import { ViewBookingModal } from '../frontdesk/components/ViewBookingModal';
import { FrontDeskBooking } from '../../types';

const { Title, Text } = Typography;

const GuestCRM: React.FC = () => {
  const [crmGuests, setCrmGuests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  
  const [viewGuestModalOpen, setViewGuestModalOpen] = useState(false);
  const [selectedGuestProfile, setSelectedGuestProfile] = useState<any | null>(null);
  
  const [viewBookingModalOpen, setViewBookingModalOpen] = useState(false);
  const [selectedViewBooking, setSelectedViewBooking] = useState<any | null>(null);

  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [offers, setOffers] = useState<any[]>([]);

  useEffect(() => {
    fetchGuests();
    fetchAuxData();
  }, []);

  const fetchAuxData = async () => {
    try {
      const [rtRes, offRes] = await Promise.all([
        adminRoomService.getAllRoomTypes(),
        offersService.getOffers()
      ]);
      setRoomTypes((rtRes as any).data?.data || (rtRes as any).data || []);
      setOffers((offRes as any).data || []);
    } catch (err) {
      console.error("Failed to fetch aux data", err);
    }
  };

  const fetchGuests = async () => {
    try {
      setLoading(true);
      const response = await adminOperationsService.getCrmGuests();
      setCrmGuests(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  const crmColumns = useMemo(
    () => [
      { 
        title: 'Guest', 
        dataIndex: 'full_name', 
        key: 'full_name',
        sorter: (a: any, b: any) => (a.full_name || '').localeCompare(b.full_name || '')
      },
      { title: 'Email', dataIndex: 'email', key: 'email' },
      { title: 'Total Stays', dataIndex: 'total_stays', key: 'total_stays' },
      {
        title: 'Total Spent',
        dataIndex: 'total_spent',
        key: 'total_spent',
        render: (value: number) => formatLkr(Number(value || 0)),
        sorter: (a: any, b: any) => Number(a.total_spent) - Number(b.total_spent)
      },
      { title: 'Last Stay', dataIndex: 'last_stay_date', key: 'last_stay_date' , render: (val: string) => val ? dayjs(val).format('MMM D, YYYY') : 'N/A' }
    ],
    []
  );

  const filteredGuests = crmGuests.filter((guest) => 
    guest.full_name?.toLowerCase().includes(searchText.toLowerCase()) || 
    guest.email?.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleOpenViewBooking = useCallback((booking: FrontDeskBooking) => {
    setSelectedViewBooking(booking);
    setViewBookingModalOpen(true);
  }, []);

  return (
    <div className="guest-crm-container">
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Guest CRM Overview</Title>
          <Text type="secondary">Manage guest profiles, view total stays and engagement segments.</Text>
        </div>
        <Space>
          <Input 
            placeholder="Search by name or email" 
            prefix={<SearchOutlined />} 
            allowClear
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
          <Button onClick={fetchGuests} loading={loading}>Refresh</Button>
        </Space>
      </div>

      <Card>
        <Table 
          rowKey="id" 
          dataSource={filteredGuests} 
          columns={crmColumns} 
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => {
              setSelectedGuestProfile(record);
              setViewGuestModalOpen(true);
            },
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <ViewGuestProfileModal
        open={viewGuestModalOpen}
        guestProfile={selectedGuestProfile}
        onCancel={() => {
          setViewGuestModalOpen(false);
          setSelectedGuestProfile(null);
        }}
        onRefresh={fetchGuests}
        onViewBooking={handleOpenViewBooking}
      />

      <ViewBookingModal
        open={viewBookingModalOpen}
        onCancel={() => setViewBookingModalOpen(false)}
        booking={selectedViewBooking}
        roomTypes={roomTypes}
        offers={offers}
        onEdit={() => message.info('Please edit bookings from the Front Desk tab.')}
        onDelete={() => message.info('Please delete bookings from the Front Desk tab.')}
        onPrintReceipt={async (bookingId) => {
          try {
            const token = localStorage.getItem("admin_token");
            const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";
            const response = await fetch(`${baseUrl}/front-desk/bookings/${bookingId}/receipt`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error("Failed to fetch receipt");
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url, "_blank");
          } catch (error) {
            console.error(error);
            message.error("Failed to load receipt PDF");
          }
        }}
        onRoomCheck={() => message.info('Please do room checkouts from the Front Desk tab.')}
      />
    </div>
  );
};

export default GuestCRM;
